/* Organizer Website — optional module renderers. Registry pattern: adding a
   future module type = one renderer here + a config form in the editor; the
   structure/acceptance layers need no change. Modules render between the
   Auctions section and How-to-bid, in the organizer's configured order. */
import { ArrowUpRight, CalendarDays, Play, Quote } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { WebsiteStats } from "@/components/website/WebsiteStats";
import { mediaUrlFromMap } from "@/lib/api";
import { formatDate, localized } from "@/lib/intl";

const Section = ({ id, children, testid, alt = false }) => (
  <section id={id} className={`scroll-mt-20 ${alt ? "bg-[#FAFAF8] border-y border-black/5" : "bg-white"}`} data-testid={testid}>
    <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-20">{children}</div>
  </section>
);

const Title = ({ children }) => (
  <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-[#0A0A0A] brand-heading mb-10">{children}</h2>
);

/* ---- about: description + stats + link to the full profile page ---- */
const AboutModule = ({ data, module, locale, t }) => {
  const text = localized(module.props?.text, locale) || localized(data.organizer.description, locale);
  if (!text) return null;
  return (
    <Section id={`mod-${module.id}`} testid="website-module-about">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        <div className="flex flex-col gap-6">
          <Title>{t("web.aboutUs")}</Title>
          <p className="text-base text-[#0A0A0A]/70 leading-relaxed -mt-8 whitespace-pre-line">{text}</p>
          <Link
            to={`/${data.organizer.slug}/about`}
            data-testid="website-about-more"
            className="inline-flex items-center gap-1.5 text-sm font-medium w-fit"
            style={{ color: "var(--brand-accent, #1D4D3B)" }}
          >
            {t("web.learnMore")}
            <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </div>
        <WebsiteStats stats={data.stats} config={data.website?.stats} />
      </div>
    </Section>
  );
};

/* ---- gallery ---- */
const GalleryModule = ({ data, module, t }) => {
  const urls = (module.props?.media_ids || []).map((id) => mediaUrlFromMap(data.media, id)).filter(Boolean);
  if (!urls.length) return null;
  return (
    <Section id={`mod-${module.id}`} testid="website-module-gallery" alt>
      <Title>{t("web.gallery")}</Title>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {urls.map((url, i) => (
          <img key={i} src={url} alt="" loading="lazy" className={`w-full object-cover rounded-2xl border border-black/5 ${i % 3 === 0 ? "h-64" : "h-48"}`} />
        ))}
      </div>
    </Section>
  );
};

/* ---- sponsors (organizer sponsor docs) + partners (module-configured) ---- */
const LogoRow = ({ items, media, testid }) =>
  items.length > 0 && (
    <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-8" data-testid={testid}>
      {items.map((p, i) => {
        const url = mediaUrlFromMap(media, p.logo_media_id);
        const inner = url ? (
          <img src={url} alt={p.name} loading="lazy" className="h-10 sm:h-12 max-w-[180px] w-auto object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-[filter,opacity] duration-300" />
        ) : (
          <span className="text-xl font-medium tracking-tight text-[#0A0A0A]/35 hover:text-[#0A0A0A]/70 transition-colors duration-300">{p.name}</span>
        );
        return p.url ? (
          <a key={i} href={p.url} target="_blank" rel="noopener noreferrer sponsored" aria-label={p.name}>{inner}</a>
        ) : (
          <span key={i}>{inner}</span>
        );
      })}
    </div>
  );

const SponsorsModule = ({ data, module, t }) => {
  const sponsors = data.sponsors || [];
  if (!sponsors.length) return null;
  return (
    <Section id={`mod-${module.id}`} testid="website-module-sponsors">
      <span className="block text-center text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0A0A0A]/40 mb-10">{t("web.sponsors")}</span>
      <LogoRow items={sponsors} media={data.media} testid="website-sponsors-row" />
    </Section>
  );
};

const PartnersModule = ({ data, module, t }) => {
  const items = module.props?.items || [];
  if (!items.length) return null;
  return (
    <Section id={`mod-${module.id}`} testid="website-module-partners">
      <span className="block text-center text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0A0A0A]/40 mb-10">{t("web.partners")}</span>
      <LogoRow items={items} media={data.media} testid="website-partners-row" />
    </Section>
  );
};

/* ---- testimonials ---- */
const TestimonialsModule = ({ data, module, locale, t }) => {
  const items = module.props?.items || [];
  if (!items.length) return null;
  return (
    <Section id={`mod-${module.id}`} testid="website-module-testimonials" alt>
      <Title>{t("web.testimonials")}</Title>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((item, i) => (
          <figure key={i} className="rounded-2xl border border-black/5 bg-white p-7 flex flex-col gap-5" data-testid={`website-testimonial-${i}`}>
            <Quote className="w-6 h-6 opacity-30" style={{ color: "var(--brand-accent, #1D4D3B)" }} aria-hidden="true" />
            <blockquote className="text-[15px] text-[#0A0A0A]/80 leading-relaxed flex-1">{localized(item.quote, locale)}</blockquote>
            <figcaption className="flex flex-col">
              <span className="text-sm font-semibold text-[#0A0A0A]">{item.author}</span>
              {item.role && <span className="text-xs text-[#737373]">{localized(item.role, locale)}</span>}
            </figcaption>
          </figure>
        ))}
      </div>
    </Section>
  );
};

/* ---- faq ---- */
const FaqModule = ({ module, locale, t }) => {
  const items = module.props?.items || [];
  if (!items.length) return null;
  return (
    <Section id={`mod-${module.id}`} testid="website-module-faq">
      <Title>{t("web.faq")}</Title>
      <Accordion type="single" collapsible className="max-w-3xl">
        {items.map((item, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="border-black/5">
            <AccordionTrigger className="text-left text-base font-medium text-[#0A0A0A] hover:no-underline">
              {localized(item.q, locale)}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-[#737373] leading-relaxed">{localized(item.a, locale)}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Section>
  );
};

/* ---- news ---- */
const NewsModule = ({ data, module, locale, t }) => {
  const items = module.props?.items || [];
  if (!items.length) return null;
  return (
    <Section id={`mod-${module.id}`} testid="website-module-news" alt>
      <Title>{t("web.news")}</Title>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {items.map((item, i) => {
          const img = mediaUrlFromMap(data.media, item.media_id);
          const body = (
            <article className="rounded-2xl border border-black/5 bg-white overflow-hidden flex flex-col h-full hover:-translate-y-1 hover:shadow-[0_16px_40px_rgb(0,0,0,0.08)] transition-[transform,box-shadow] duration-300">
              {img && <img src={img} alt="" loading="lazy" className="h-44 w-full object-cover" />}
              <div className="p-6 flex flex-col gap-2 flex-1">
                {item.date && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#737373]">
                    <CalendarDays className="w-3.5 h-3.5" aria-hidden="true" />
                    {formatDate(item.date, locale)}
                  </span>
                )}
                <h3 className="text-lg font-medium text-[#0A0A0A] leading-snug">{localized(item.title, locale)}</h3>
                {item.body && <p className="text-sm text-[#737373] leading-relaxed line-clamp-3">{localized(item.body, locale)}</p>}
              </div>
            </article>
          );
          return item.url ? (
            <a key={i} href={item.url} target="_blank" rel="noopener noreferrer" data-testid={`website-news-${i}`}>{body}</a>
          ) : (
            <div key={i} data-testid={`website-news-${i}`}>{body}</div>
          );
        })}
      </div>
    </Section>
  );
};

/* ---- videos ---- */
const VideosModule = ({ data, module, locale, t }) => {
  const items = (module.props?.items || [])
    .map((v) => ({ ...v, url: v.url || mediaUrlFromMap(data.media, v.media_id) }))
    .filter((v) => v.url);
  if (!items.length) return null;
  return (
    <Section id={`mod-${module.id}`} testid="website-module-videos">
      <Title>{t("web.videos")}</Title>
      <div className={`grid grid-cols-1 gap-6 ${items.length > 1 ? "md:grid-cols-2" : "max-w-3xl"}`}>
        {items.map((v, i) => (
          <figure key={i} className="rounded-2xl overflow-hidden bg-[#0A0A0A]" data-testid={`website-video-${i}`}>
            <video src={v.url} controls playsInline preload="metadata" className="w-full aspect-video object-contain bg-[#0A0A0A]">
              <track kind="captions" />
            </video>
            {v.title && (
              <figcaption className="px-5 py-3 text-sm text-white/80 flex items-center gap-2">
                <Play className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                {localized(v.title, locale)}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </Section>
  );
};

/* ---- custom ---- */
const CustomModule = ({ data, module, locale }) => {
  const title = localized(module.props?.title, locale);
  const body = localized(module.props?.body, locale);
  const img = mediaUrlFromMap(data.media, module.props?.media_id);
  if (!title && !body) return null;
  return (
    <Section id={`mod-${module.id}`} testid={`website-module-custom-${module.id}`}>
      <div className={`grid grid-cols-1 gap-10 items-center ${img ? "lg:grid-cols-2" : ""}`}>
        <div className="flex flex-col gap-5">
          {title && <Title>{title}</Title>}
          {body && <p className="text-base text-[#0A0A0A]/70 leading-relaxed -mt-8 whitespace-pre-line">{body}</p>}
        </div>
        {img && <img src={img} alt="" loading="lazy" className="w-full h-80 object-cover rounded-3xl border border-black/5" />}
      </div>
    </Section>
  );
};

const MODULE_RENDERERS = {
  about: AboutModule,
  gallery: GalleryModule,
  sponsors: SponsorsModule,
  partners: PartnersModule,
  testimonials: TestimonialsModule,
  faq: FaqModule,
  news: NewsModule,
  videos: VideosModule,
  custom: CustomModule,
};

export const WebsiteModules = ({ data }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const modules = (data.website?.modules || []).filter((m) => m.enabled);
  return modules.map((m) => {
    const Renderer = MODULE_RENDERERS[m.type];
    return Renderer ? <Renderer key={m.id} data={data} module={m} locale={locale} t={t} /> : null;
  });
};
